import {
  AssessmentAverageResult,
  AssessmentAverageSubjectResult,
  GetAssessmentsAverageParams,
  useGetAssessmentsAverage,
} from '@schooly/api';
import { useFlag } from '@schooly/hooks/use-flag';
import { usePrevious } from '@schooly/hooks/use-previous';
import { useCallback, useEffect, useState } from 'react';

import { MarkbookViewSettings } from '../MarkbookSettings/MarkbookSettings';

type UseAssessmentAverageDataProps = {
  requestParams: GetAssessmentsAverageParams;
  canFetchAverage: boolean;
  settings?: MarkbookViewSettings;
};

const getBenchmarkAverageData = (
  data: AssessmentAverageResult[],
  oldData?: AssessmentAverageResult[],
) => {
  return data.map((result, index) => ({
    ...result,
    average_value: oldData ? (oldData?.[index]?.average_value ?? null) : null,
  }));
};

const getBenchmarkSubjectAverageData = (
  data: AssessmentAverageSubjectResult[],
  oldData?: AssessmentAverageSubjectResult[],
) => {
  return data.map((result, index) => ({
    ...result,
    average_score: oldData ? (oldData?.[index]?.average_score ?? null) : null,
  }));
};

export const useAssessmentsAverage = ({
  requestParams,
  canFetchAverage,
  settings,
}: UseAssessmentAverageDataProps) => {
  const [benchmarkEnabled, enableBenchmark, disableBenchmark] = useFlag();
  const [benchmarkAverageData, setBenchmarkAverageData] = useState<AssessmentAverageResult[]>([]);
  const [benchmarkSubjectAverageData, setBenchmarkSubjectAverageData] = useState<
    AssessmentAverageSubjectResult[]
  >([]);
  const {
    data: averageData,
    isLoading: isAverageLoading,
    refetch: refetchAverage,
  } = useGetAssessmentsAverage(
    {
      query: requestParams.query,
      params: requestParams.params,
    },
    {
      enabled: canFetchAverage,
      refetchOnMount: 'always',
      onSuccess: (data) => {
        setBenchmarkAverageData((prev) => getBenchmarkAverageData(data.results, prev));
        setBenchmarkSubjectAverageData((prev) =>
          getBenchmarkSubjectAverageData(data.average_results ?? [], prev),
        );
      },
    },
  );

  const previousAvgResults = usePrevious(settings?.avgResults);

  const setBenchmarkEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        if (!averageData && !isAverageLoading) refetchAverage();
        enableBenchmark();
      } else {
        disableBenchmark();
        setBenchmarkAverageData(getBenchmarkAverageData(averageData?.results ?? []));
        setBenchmarkSubjectAverageData(
          getBenchmarkSubjectAverageData(averageData?.average_results ?? []),
        );
      }
    },
    [averageData, disableBenchmark, enableBenchmark, isAverageLoading, refetchAverage],
  );

  const setBenchmarkAverage = useCallback((average: AssessmentAverageResult) => {
    setBenchmarkAverageData((prev) =>
      prev.map((item) => {
        if (item.method.id === average.method.id && item.assessment_id === average.assessment_id) {
          return {
            ...item,
            average_value: average.average_value,
          };
        }
        return item;
      }),
    );
  }, []);

  const setBenchmarkSubjectAverage = useCallback((average: AssessmentAverageSubjectResult) => {
    setBenchmarkSubjectAverageData((prev) =>
      prev.map((item) => {
        if (item.subject_id === average.subject_id) {
          return average;
        }
        return item;
      }),
    );
  }, []);

  useEffect(() => {
    if (!!previousAvgResults && !settings?.avgResults && benchmarkEnabled) {
      setBenchmarkEnabled(false);
    }
  }, [benchmarkEnabled, previousAvgResults, settings?.avgResults, setBenchmarkEnabled]);

  return {
    averageData: benchmarkEnabled ? benchmarkAverageData : (averageData?.results ?? []),
    subjectAverageData: benchmarkEnabled
      ? benchmarkSubjectAverageData
      : (averageData?.average_results ?? []),
    isAverageLoading: isAverageLoading && !benchmarkEnabled,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    setBenchmarkSubjectAverage,
    benchmarkEnabled,
  };
};
